import { Injectable, inject } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";

import { AggregateHelperService, IAggregateActionHandler } from "@tylertech/aggregates-module";
import { IAggregate, ICardData, ICardAction, ExecutionResult } from "@tylertech/tyler-api-types";
import { TylerSmartMeterTextEmailDialogComponent, TylerSmartMeterTextEmailDialogData } from "../components";
import { IShellService } from "@tylertech/incode-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { NavigationService } from "@tylertech/core-module";

@Injectable()
export class LeakNotificationNotSentActionHandler implements IAggregateActionHandler
{
    private readonly matDialog = inject(MatDialog);
    private readonly $navigationService = inject(NavigationService);
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $shellService = inject(IShellService);
    private readonly $aggregateHelperService = inject(AggregateHelperService);


    predicate(aggregate: IAggregate, _cardData: ICardData, _action: ICardAction): boolean
    {
        return aggregate.AggregateProviderId === "Utility.LeakNotificationNotSentService";
    }

    execute(aggregate: IAggregate, cardData: ICardData, action: ICardAction): Promise<boolean>
    {
        if (action.Id === "TSMNotify")
        {
            const dialogConfig: MatDialogConfig<TylerSmartMeterTextEmailDialogData> = {
                data: {
                    accountNumber: this.getField(cardData, "Account Number"),
                    landlordAccountNumber: this.getField(cardData, "Landlord Account Number"),
                    alertId: this.getField(cardData, "AlertProcessingResultId"),
                    email: this.getField(cardData, "EmailAddress"),
                    sms: this.getField(cardData, "SMS"),
                    phone: this.getField(cardData, "Phone Number"),
                    landlordEmail: this.getField(cardData, "Landlord Email Address"),
                    landlordSms: this.getField(cardData, "Landlord SMS"),
                    landlordPhone: this.getField(cardData, "Landlord Phone Number")
                }
            };
            const dialogRef = this.matDialog.open(TylerSmartMeterTextEmailDialogComponent, dialogConfig);

            return dialogRef.afterClosed().toPromise();
        }
        else if (action.Id === "TSMLandlordNotify")
        {
            const dialogConfig: MatDialogConfig<TylerSmartMeterTextEmailDialogData> = {
                data: {
                    accountNumber: this.getField(cardData, "Account Number"),
                    landlordAccountNumber: this.getField(cardData, "Landlord Account Number"),
                    alertId: this.getField(cardData, "AlertProcessingResultId"),
                    email: this.getField(cardData, "EmailAddress"),
                    sms: this.getField(cardData, "SMS"),
                    phone: this.getField(cardData, "Phone Number"),
                    landlordEmail: this.getField(cardData, "Landlord Email Address"),
                    landlordSms: this.getField(cardData, "Landlord SMS"),
                    landlordPhone: this.getField(cardData, "Landlord Phone Number")
                }
            };
            const dialogRef = this.matDialog.open(TylerSmartMeterTextEmailDialogComponent, dialogConfig);

            return dialogRef.afterClosed().toPromise();
        }
        else if (action.Id === "Dismiss")
        {
            this.dismissAggregate(aggregate, cardData, action);
            return Promise.resolve(true);
        }
        else if (action.Id === "AccountResearchNewTabAction")
        {
            this.launchAccountResearch(aggregate, cardData, action);
            return Promise.resolve(true);
        }
    }

    private getField(cardData: ICardData, fieldName: string): string | undefined
    {
        return cardData.DataElements?.find(x => x.Label === fieldName)?.Value;
    }

    load(_aggregate: IAggregate, _cardData: ICardData, _action: ICardAction): void
    {
        /* NO_OP */
    }


    private dismissAggregate(aggregate: IAggregate, cardData: ICardData, action: ICardAction): void
    {
        const json = JSON.stringify({
            AlertProcessingResultId: this.$aggregateHelperService.getFieldValue(cardData, "AlertProcessingResultId"),
        });

        (this.$shellService.isEmbedded()
            ? this.$shellService.executeShellFunction(action.content, json, false)
            : this.$aggregateHelperService.performServerCardAction(aggregate, cardData, action))
            .catch(
                (result: ExecutionResult<string>) =>
                {
                    return this.$commonDialogsService.showAlertHtmlMessage(
                        "Failed to dismiss.<br>" + result.Result,
                        "Error");
                });
    }

    private launchAccountResearch(aggregate: IAggregate, cardData: ICardData, action: ICardAction): void
    {
        if (!action.content)
        {
            return;
        }

        const meterNumber = this.$aggregateHelperService.getFieldValue(cardData, "MeterNumber");
        const meterGroupId = this.$aggregateHelperService.getFieldValue(cardData, "MeterGroupId");
        const miuId = this.$aggregateHelperService.getFieldValue(cardData, "MIU");
        const alertProcessingResultId = this.$aggregateHelperService.getFieldValue(cardData, "AlertProcessingResultId");

        const input = JSON.stringify([
            meterNumber,
            meterGroupId,
            miuId,
            alertProcessingResultId,
            "true"
        ]);

        if (this.$shellService.isEmbedded())
        {
            this.$shellService.openShellFunction(action.content, input, false, "Failed to launch Account Research")
                .then(() => window.location.reload());

            return;
        }
        const queryParams: Record<string, string> = {
            meterNumber: meterNumber,
            meterGroupId: meterGroupId,
            meterRemoteReadId: miuId,
            alertProcessingResultId: alertProcessingResultId,
        };

        queryParams["resendAlert"] = "true";

        this.$navigationService.openApp("accountresearch/", "_blank", queryParams);

        window.location.reload();
    }

}
